import axios from 'axios'
import { ivInstance } from '@/components/indoorviewer/IndoorviewerView.vue'
import store from '@/store'

// Checking if the site model entity (level or related rooms) has panorama images
// For some reason, floors are not always connected to it's panoramas, additional search in rooms due to that
export function checkForPanoramaImages(levelId, images, siteModel) {
  let hasPanorama = false
  const matchingImages = images.filter((image) => image.site_model_entity_id === levelId)
  if (!matchingImages.length) {
    let levelInSiteModel = siteModel[0].children.find((level) => level.id === levelId)
    if (levelInSiteModel?.children.length) {
      levelInSiteModel.children.forEach((room) => {
        if (images.find((image) => room.id === image.site_model_entity_id)) hasPanorama = true
      })
    }
  }
  if (matchingImages.length) hasPanorama = true
  return hasPanorama
}

export async function getIndoorviewerImages(navvisInstanceURL, token) {
  const images = await navvisRestApiRequest(navvisInstanceURL, 'api/images', token)
  return images
}

// Fetching token from a specific Navvis Instance
export async function getNavvisApiToken(navvisURL) {
  const headers = {
    'Access-Control-Allow-Origin': '*',
    Authorization: 'Bearer ' + store.state.oidcStore.access_token
  }
  return new Promise((resolve, reject) => {
    axios
      .get(`${process.env.VUE_APP_API_BASE_URL}/v1/auth/token/ivion`, {
        headers: headers,
        params: {
          ivionUrl: navvisURL
        }
      })
      .then((response) => {
        // Ivion response also contains refresh_token and upload_token
        const token = response.data.access_token
        resolve(token)
      })
      .catch((error) => {
        reject(error)
      })
  })
}

export async function getNavvisVersion(navvisInstanceURL, token) {
  let version = await navvisRestApiRequest(navvisInstanceURL, 'version.json', token)
  return version
}

function getRoomsFromSiteModel(obj, input) {
  let children = obj.flatMap((x) => x.children)
  while (children && children.length > 0) {
    if (children.some((x) => x.type === 'ROOM')) {
      children = children.filter((x) => x.name.toLowerCase().includes(input.toLowerCase()))
      return children
    }
    if (children.some((x) => x.type === 'FLOOR')) {
      // eslint-disable-next-line no-return-assign
      children = children.flatMap((x) =>
        x.children.map((i) => {
          i.floor = x.name
          return i
        })
      )
    } else if (children.some((x) => x.floor)) {
      children = children.flatMap((x) =>
        x.children.map((i) => {
          i.floor = x.floor
          return i
        })
      )
    } else {
      children = children.flatMap((x) => x.children)
    }
  }
  return children || []
}
export function goToRoom(id) {
  return ivInstance.moveToSiteModelEntityId(id, false, undefined)
}
export function goToPOI(poi) {
  return ivInstance.moveToPoiId(poi.poi.id, true)
}

/* Search for rooms and POIs matching the search string. Returns all roms and matching POIs as an array with objects */
// A valid POI should contain title, building, room, floor, description, poiType
export async function navvisSearchRequest(instanceURL, input, token) {
  let siteModelSearch = await navvisRestApiRequest(instanceURL, 'api/site_model', token)
  let searchedRooms = getRoomsFromSiteModel(siteModelSearch, input)
  let searchedPois = await navvisPoiSearchRequest(instanceURL, input, 0, 6, token)
  return [searchedPois, searchedRooms]
}
export async function navvisPoiSearchRequest(instanceURL, input, from, size, token) {
  let searchedPois = await navvisRestApiRequest(
    instanceURL,
    'api/search/poi?from=' + from + '&query=' + input + '&lang=en&size=' + size,
    token
  )
  for (let i = 0; i < searchedPois.length; i++) {
    try {
      let fullPoiDetails = await navvisRestApiRequest(
        instanceURL,
        'api/pois/filter?hidden=false&lat=' +
          searchedPois[i].scs_location[1] +
          '&limit=1&lon=' +
          searchedPois[i].scs_location[0] +
          '&site_model_entity=' +
          searchedPois[i].site_model_entity_id +
          '&z=' +
          searchedPois[i].scs_location[2],
        token
      )
      searchedPois[i].description = fullPoiDetails[0].descriptions.sv
      let pointInformation = await navvisRestApiRequest(
        instanceURL,
        'api/site_model/within?location=' +
          searchedPois[i].scs_location[0] +
          '&location=' +
          searchedPois[i].scs_location[1] +
          '&location=' +
          searchedPois[i].scs_location[2],
        token
      )
      searchedPois[i].building = pointInformation[0].name
      searchedPois[i].floor = pointInformation[0].children[0].name
      let poiType = await navvisRestApiRequest(
        instanceURL,
        'api/poi_types/' + searchedPois[i].poi_type_id,
        token
      )
      searchedPois[i].poiType = poiType
      searchedPois[i].icon = poiType.icon
      let siteModelEntityDetails = await navvisRestApiRequest(
        instanceURL,
        'api/site_model/' + searchedPois[i].site_model_entity_id,
        token
      )
      searchedPois[i].siteModelEntity = siteModelEntityDetails
      searchedPois[i].room = siteModelEntityDetails.name
    } catch (e) {
      console.error(e)
    }
  }
  return searchedPois
}

/* Changing the point cloud visibility. TODO: change to their solution, aka do NOT keep IV.injector... */
export async function setPointCloudVisibility(visible) {
  ivInstance.injector.get('PointCloudMenuService').showPointClouds(visible)
}
/* Changing the point cloud visibility. TODO: change to their solution, aka do NOT keep IV.injector... */
export async function setPanoramaVisibility(visible) {
  ivInstance.injector.get('ViewMenuService').showPanoramas(visible)
}
/* Changing the point cloud visibility. TODO: change to their solution, aka do NOT keep IV.injector... */
export async function setLocationMarkersVisibility(visible) {
  ivInstance.injector.get('ViewMenuService').showLocationMarkers(visible)
}
/* The general function to use with NavVis REST API. Returns the data. */
export async function navvisRestApiRequest(instanceURL, endpoint, token) {
  // Using promisies in order to handle asynchrondous axios calls.
  endpoint = `${endpoint.slice(0, 4)}site/${instanceURL.split('site=')[1]}${endpoint.slice(4)}`
  instanceURL = instanceURL.split('?')[0]
  return new Promise((resolve, reject) => {
    axios
      .get(instanceURL + endpoint, {
        headers: {
          'X-Authorization': 'Bearer ' + token
        }
      })
      .then((response) => {
        resolve(response.data)
      })
      .catch((error) => {
        reject(error)
      })
  })
}
